import {
  ColumnLayout,
  FormField,
  Input,
  Select,
} from '@cloudscape-design/components';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import * as Yup from 'yup';

const PaymentTypeMaps = Object.freeze({
  RECEIVER: { label: 'Receiver', value: 'RECEIVER' },
  THIRD_PARTY: { label: 'Third Party', value: 'THIRD_PARTY' },
  SENDER: { label: 'Parallel Fluidics', value: 'SENDER' },
  COLLECT: { label: 'Collect', value: 'COLLECT' },
});

const ValidationSchema = Yup.object({
  type: Yup.string()
    .oneOf(['RECEIVER', 'THIRD_PARTY', 'SENDER', 'COLLECT'], 'Invalid type')
    .required('Type is required'),
  account: Yup.string()
    .when('type', {
      is: (type) => type === 'RECEIVER' || type === 'THIRD_PARTY',
      then: Yup.string().required('Account is required'),
      otherwise: Yup.string().nullable(),
    }),
  country: Yup.string()
    .when('type', {
      is: 'THIRD_PARTY',
      then: Yup.string().required('Country is required for THIRD_PARTY'),
      otherwise: Yup.string().nullable(),
    }),
  postal_code: Yup.string()
    .when('type', {
      is: (type) => type === 'RECEIVER' || type === 'THIRD_PARTY',
      then: Yup.string().required('Postal code is required for RECEIVER or THIRD_PARTY'),
      otherwise: Yup.string().nullable(),
    }),
});

export default function PaymentInputs({
  onChange,
  defaultValues = {
    type: '',
    account: '',
    country: '',
    postal_code: '',
  },
}) {
  const {
    values, errors, setFieldValue, isValid, validateForm,
  } = useFormik({
    initialValues: defaultValues,
    validationSchema: ValidationSchema,
    validateOnMount: false,
    validateOnChange: false,
  });

  useEffect(() => {
    validateForm();
    onChange?.(values, isValid);
  }, [values, isValid, onChange, validateForm]);

  return (
    <ColumnLayout columns={2}>
      <FormField label="Payer" errorText={errors.type}>
        <Select
          selectedOption={PaymentTypeMaps[values.type]}
          onChange={({ detail }) => {
            setFieldValue('type', detail.selectedOption.value);
            if (detail.selectedOption.value === 'SENDER') {
              setFieldValue('account', '');
              setFieldValue('country', '');
              setFieldValue('postal_code', '');
            }
          }}
          options={Object.values(PaymentTypeMaps)}
        />
      </FormField>
      {values.type !== 'SENDER' && (
        <>
          <FormField label="Account" errorText={errors.account}>
            <Input
              value={values.account}
              onChange={({ detail }) => { setFieldValue('account', detail.value); }}
            />
          </FormField>
          <FormField label="Country" errorText={errors.country}>
            <Input
              value={values.country}
              onChange={({ detail }) => { setFieldValue('country', detail.value); }}
            />
          </FormField>
          <FormField label="Postal code" errorText={errors.postal_code}>
            <Input
              value={values.postal_code}
              onChange={({ detail }) => { setFieldValue('postal_code', detail.value); }}
            />
          </FormField>
        </>
      )}
    </ColumnLayout>
  );
}

PaymentInputs.propTypes = {
  onChange: PropTypes.func.isRequired,
  defaultValues: PropTypes.shape({
    type: PropTypes.string,
    account: PropTypes.string,
    country: PropTypes.string,
    postal_code: PropTypes.string,
  }),
};
